import React, { useEffect, useState } from "react";
import { Edit3, Save, Shield, Lock, Unlock, X, Users, Table, Database, Clock, Grid, RefreshCw, Trash2, Search, Filter, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown } from "lucide-react";
import Swal from "sweetalert2";

import API_BASE_URL from "../config";

export default function PositionLevelAccess() {
    const [userLevels, setUserLevels] = useState([]);
    const [positions, setPositions] = useState([]);
    const [savedAccess, setSavedAccess] = useState([]);
    const [permissions, setPermissions] = useState({});
    const [editingLevel, setEditingLevel] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Pagination & Filters
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("all"); // all, with-access, no-access
    const [sortBy, setSortBy] = useState("name"); // name, id, access-count
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(6);

    // Position search inside cards
    const [positionSearch, setPositionSearch] = useState({});

    useEffect(() => {
        fetchData();
    }, []);
    const [displayRecords, setDisplayRecords] = useState([]);

    const fetchData = async () => {
        try {
            setLoading(true);

            const levelsRes = await fetch(`${API_BASE_URL}api/UserLevels`);
            if (!levelsRes.ok) throw new Error("Failed to fetch user levels");
            const levelsData = await levelsRes.json();

            const activeLevels = levelsData.filter(l => l.isActive === 1);
            setUserLevels(activeLevels);

            const posRes = await fetch(`${API_BASE_URL}api/M_Position`);
            if (!posRes.ok) throw new Error("Failed to fetch positions");
            const posData = await posRes.json();

            const activePos = posData.filter(p => p.isActive === 1);
            setPositions(activePos);

            try {
                const accessRes = await fetch(`${API_BASE_URL}api/Role_Position_Access`);
                if (accessRes.ok) {
                    const accessData = await accessRes.json();
                    setSavedAccess(accessData);

                    const permObj = {};
                    accessData.forEach((access) => {
                        if (access.isActive) {
                            try {
                                const roleData = typeof access.role_Position === 'string' && access.role_Position.startsWith('{')
                                    ? JSON.parse(access.role_Position)
                                    : { userLevelId: access.role_Position };

                                if (roleData.userLevelId && roleData.positions) {
                                    if (!permObj[roleData.userLevelId]) {
                                        permObj[roleData.userLevelId] = {};
                                    }
                                    roleData.positions.forEach(posId => {
                                        permObj[roleData.userLevelId][posId] = true;
                                    });
                                }
                            } catch (e) {
                                console.error("Error parsing role_Position:", e);
                            }
                        }
                    });
                    const displayData = accessData.map(access => {
                        try {
                            const roleData = typeof access.role_Position === 'string' && access.role_Position.startsWith('{')
                                ? JSON.parse(access.role_Position)
                                : { userLevelName: access.role_Position, positions: [] };

                            const userLevelName = roleData.userLevelName || access.role_Position;
                            const positionCount = Array.isArray(roleData.positions) ? roleData.positions.length : 0;
                            const positionNames = Array.isArray(roleData.positions)
                                ? roleData.positions.map(posId => {
                                    const pos = activePos.find(p => p.id === posId);
                                    return pos ? pos.positionName : `ID: ${posId}`;
                                })
                                : [];

                            return {
                                id: access.id,
                                userLevelName,
                                positionCount,
                                positionNames,
                                isActive: access.isActive
                            };
                        } catch (e) {
                            return {
                                id: access.id,
                                userLevelName: access.role_Position,
                                positionCount: 0,
                                positionNames: [],
                                isActive: access.isActive
                            };
                        }
                    });

                    setDisplayRecords(displayData);

                    setPermissions(permObj);
                }
            } catch (err) {
                console.log("No saved access yet");
            }

        } catch (err) {
            console.error(err);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: `Failed to fetch data: ${err.message}`,
                confirmButtonColor: "#d33",
            });
        } finally {
            setLoading(false);
        }
    };

    const handlePermissionToggle = (userLevelId, positionId) => {
        setPermissions((prev) => ({
            ...prev,
            [userLevelId]: {
                ...prev[userLevelId],
                [positionId]: !prev[userLevelId]?.[positionId],
            },
        }));
    };

    const handleSelectAll = (userLevelId, value) => {
        const newPerms = {};
        positions.forEach(pos => {
            newPerms[pos.id] = value;
        });
        setPermissions(prev => ({
            ...prev,
            [userLevelId]: newPerms
        }));
    };

    const handleSave = async (userLevelId) => {
        const userLevel = userLevels.find(l => l.id === userLevelId);

        const result = await Swal.fire({
            title: 'Save Changes?',
            text: `Update position access for ${userLevel?.userLevelName}?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, save it!',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) return;

        try {
            setSaving(true);

            const enabledPositions = positions
                .filter(pos => permissions[userLevelId]?.[pos.id])
                .map(pos => pos.id);

            const rolePositionData = {
                userLevelId: userLevelId,
                userLevelName: userLevel?.userLevelName,
                positions: enabledPositions
            };

            console.log("📝 Saving data:", rolePositionData);

            // Check if record exists for this userLevelId
            const existingRecord = savedAccess.find(access => {
                try {
                    const roleData = typeof access.role_Position === 'string' && access.role_Position.startsWith('{')
                        ? JSON.parse(access.role_Position)
                        : null;
                    return roleData && roleData.userLevelId === userLevelId;
                } catch (e) {
                    return false;
                }
            });

            console.log("🔍 Existing record:", existingRecord);

            let response;
            const payload = {
                id: existingRecord ? existingRecord.id : 0,
                role_Position: JSON.stringify(rolePositionData),
                isActive: true
            };

            console.log("📦 Payload:", payload);

            if (existingRecord) {
                // UPDATE
                const url = `${API_BASE_URL}api/Role_Position_Access/${existingRecord.id}`;
                console.log("🔄 PUT URL:", url);

                response = await fetch(url, {
                    method: 'PUT',
                    headers: {
                        'accept': '*/*',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload)
                });
            } else {
                // CREATE
                const url = `${API_BASE_URL}api/Role_Position_Access`;
                console.log("➕ POST URL:", url);

                response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'accept': '*/*',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload)
                });
            }

            console.log("📊 Response status:", response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.error("❌ API Error Response:", errorText);
                throw new Error(`Failed to save: ${response.status} - ${errorText}`);
            }

            const responseData = await response.text();
            console.log("✅ API Success Response:", responseData);

            setEditingLevel(null);
            await fetchData();

            Swal.fire({
                icon: "success",
                title: "Access Saved!",
                text: `✅ Position access saved for ${userLevel?.userLevelName}!`,
                confirmButtonColor: "#3085d6",
                confirmButtonText: "OK",
            });
        } catch (err) {
            console.error("💥 Full Error:", err);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: `❌ Error saving access: ${err.message}`,
                confirmButtonColor: "#d33",
                confirmButtonText: "OK",
            });
        } finally {
            setSaving(false);
        }
    };

    // Filter & Sort Logic
    const getFilteredAndSortedLevels = () => {
        let filtered = [...userLevels];

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(level =>
                level.userLevelName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                level.id.toString().includes(searchTerm)
            );
        }

        // Status filter
        if (filterStatus === "with-access") {
            filtered = filtered.filter(level => {
                const count = Object.values(permissions[level.id] || {}).filter(Boolean).length;
                return count > 0;
            });
        } else if (filterStatus === "no-access") {
            filtered = filtered.filter(level => {
                const count = Object.values(permissions[level.id] || {}).filter(Boolean).length;
                return count === 0;
            });
        }

        // Sort
        filtered.sort((a, b) => {
            if (sortBy === "name") {
                return a.userLevelName.localeCompare(b.userLevelName);
            } else if (sortBy === "id") {
                return a.id - b.id;
            } else if (sortBy === "access-count") {
                const countA = Object.values(permissions[a.id] || {}).filter(Boolean).length;
                const countB = Object.values(permissions[b.id] || {}).filter(Boolean).length;
                return countB - countA;
            }
            return 0;
        });

        return filtered;
    };

    const filteredLevels = getFilteredAndSortedLevels();

    // Pagination
    const totalPages = Math.ceil(filteredLevels.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentLevels = filteredLevels.slice(startIndex, endIndex);

    const goToPage = (page) => {
        setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    };

    // Filter positions inside card
    const getFilteredPositions = (userLevelId) => {
        const search = positionSearch[userLevelId] || "";
        if (!search) return positions;

        return positions.filter(pos =>
            pos.positionName.toLowerCase().includes(search.toLowerCase())
        );
    };

    const getUserLevelIcon = (index) => {
        const icons = [Users, Shield, Table, Database, Clock, Grid];
        return icons[index % icons.length];
    };

    const getUserLevelIconBg = (index) => {
        const colors = [
            "from-blue-400 to-blue-600",
            "from-cyan-400 to-cyan-600",
            "from-sky-400 to-sky-600",
            "from-indigo-400 to-indigo-600",
            "from-blue-500 to-blue-700",
            "from-teal-400 to-teal-600",
        ];
        return colors[index % colors.length];
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-100 via-white to-cyan-100">
                <div className="flex flex-col items-center gap-4 bg-white/40 backdrop-blur-xl border border-white/60 p-8 rounded-3xl shadow-2xl">
                    <div className="w-16 h-16 border-4 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
                    <p className="text-gray-700 text-lg font-bold">Loading user levels...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen  from-blue-100 via-white to-cyan-100 p-6">
            <div className="max-w-8xl mx-auto">
                {/* Header */}
                <div className="mb-6 bg-white/40 backdrop-blur-xl border border-white/60 rounded-3xl p-6 shadow-2xl">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800 mb-2">Position Level Access</h1>
                            <p className="text-gray-600">Configure position access for each user level</p>
                        </div>
                        <button
                            onClick={fetchData}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
                        >
                            <RefreshCw size={18} />
                            <span>Refresh</span>
                        </button>
                    </div>

                    {/* Search & Filters */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {/* Search */}
                        <div className="md:col-span-2">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                                <input
                                    type="text"
                                    placeholder="Search by name or ID..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="w-full pl-10 pr-4 py-2.5 bg-white/60 backdrop-blur-sm border border-white/80 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all"
                                />
                            </div>
                        </div>

                        {/* Filter Status */}
                        <div className="relative">
                            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                            <select
                                value={filterStatus}
                                onChange={(e) => {
                                    setFilterStatus(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="w-full pl-10 pr-4 py-2.5 bg-white/60 backdrop-blur-sm border border-white/80 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all appearance-none cursor-pointer"
                            >
                                <option value="all">All Status</option>
                                <option value="with-access">With Access</option>
                                <option value="no-access">No Access</option>
                            </select>
                        </div>

                        {/* Sort */}
                        <div className="relative">
                            <ArrowUpDown className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 bg-white/60 backdrop-blur-sm border border-white/80 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all appearance-none cursor-pointer"
                            >
                                <option value="name">Sort by Name</option>
                                <option value="id">Sort by ID</option>
                                <option value="access-count">Sort by Access Count</option>
                            </select>
                        </div>
                    </div>

                    {/* Results Count */}
                    <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
                        <span>
                            Showing <strong>{currentLevels.length}</strong> of <strong>{filteredLevels.length}</strong> user levels
                        </span>
                        <div className="flex items-center gap-2">
                            <span>Items per page:</span>
                            <select
                                value={itemsPerPage}
                                onChange={(e) => {
                                    setItemsPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                                className="px-2 py-1 bg-white/60 border border-white/80 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                            >
                                <option value={3}>3</option>
                                <option value={6}>6</option>
                                <option value={9}>9</option>
                                <option value={12}>12</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Cards Grid */}
                {currentLevels.length === 0 ? (
                    <div className="bg-white/40 backdrop-blur-xl border border-white/60 rounded-3xl p-12 shadow-2xl text-center">
                        <Database className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                        <h3 className="text-xl font-bold text-gray-700 mb-2">No Results Found</h3>
                        <p className="text-gray-600">Try adjusting your search or filter criteria</p>
                    </div>
                ) : (
                    <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-6 mb-6">
                        {currentLevels.map((userLevel, index) => {
                            const isEditing = editingLevel === userLevel.id;
                            const enabledCount = Object.values(permissions[userLevel.id] || {}).filter(Boolean).length;
                            const IconComponent = getUserLevelIcon(index);
                            const filteredPositionsForCard = getFilteredPositions(userLevel.id);

                            return (
                                <div
                                    key={userLevel.id}
                                    className={`relative rounded-3xl bg-white/40 backdrop-blur-2xl border border-white/60 shadow-2xl p-6 transition-all duration-500 hover:shadow-[0_20px_60px_-15px_rgba(59,130,246,0.5)] hover:scale-[1.03] hover:bg-white/50 ${isEditing ? "ring-2 ring-blue-400/60 shadow-[0_20px_60px_-15px_rgba(59,130,246,0.6)]" : ""
                                        }`}
                                    style={{ backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)" }}
                                >
                                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-transparent to-cyan-50/50 rounded-3xl pointer-events-none"></div>

                                    <div className="relative z-10">
                                        <div className={`w-16 h-16 bg-gradient-to-br ${getUserLevelIconBg(index)} rounded-2xl flex items-center justify-center mb-4 shadow-xl`}>
                                            <IconComponent className="w-8 h-8 text-white" strokeWidth={2.5} />
                                        </div>

                                        <h3 className="text-xl font-bold text-gray-800 uppercase mb-2 tracking-wide">{userLevel.userLevelName}</h3>
                                        <p className="text-sm text-gray-600 mb-5">User Level ID: {userLevel.id}</p>
                                        <div className="border-t border-gray-300/50 mb-4"></div>

                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-2 text-sm text-gray-700 font-medium">
                                                <div className={`w-2.5 h-2.5 rounded-full shadow-lg ${enabledCount > 0 ? 'bg-emerald-500 shadow-emerald-500/50 animate-pulse' : 'bg-gray-400 shadow-gray-400/50'}`}></div>
                                                <span>{enabledCount > 0 ? `${enabledCount} Position${enabledCount > 1 ? 's' : ''}` : 'No Positions'}</span>
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => (isEditing ? handleSave(userLevel.id) : setEditingLevel(userLevel.id))}
                                            disabled={saving}
                                            className="flex items-center gap-2 text-sm text-gray-700 hover:text-blue-600 transition-all duration-300 font-medium group disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {isEditing ? (
                                                <>
                                                    <div className="p-1.5 rounded-lg bg-blue-100/80 group-hover:bg-blue-200/80 transition-colors">
                                                        <Save size={16} className="group-hover:scale-110 transition-transform" />
                                                    </div>
                                                    <span>{saving ? "Saving..." : "Save Access"}</span>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="p-1.5 rounded-lg bg-gray-100/80 group-hover:bg-blue-100/80 transition-colors">
                                                        <Edit3 size={16} className="group-hover:rotate-12 transition-transform" />
                                                    </div>
                                                    <span>Edit Access</span>
                                                </>
                                            )}
                                        </button>

                                        {isEditing && (
                                            <div className="mt-4 bg-white/60 backdrop-blur-xl p-4 rounded-2xl border border-white/60 shadow-inner">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="text-xs text-gray-600 font-semibold">
                                                        <span className="text-blue-600 text-sm">{enabledCount}</span>
                                                        <span className="text-gray-400"> / {positions.length}</span>
                                                    </div>
                                                    <button
                                                        onClick={() => setEditingLevel(null)}
                                                        className="p-1.5 hover:bg-white/80 rounded-lg transition-all duration-300 hover:rotate-90"
                                                    >
                                                        <X size={16} className="text-gray-600" />
                                                    </button>
                                                </div>

                                                {/* Position Search */}
                                                <div className="relative mb-3">
                                                    <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                                                    <input
                                                        type="text"
                                                        placeholder="Search positions..."
                                                        value={positionSearch[userLevel.id] || ""}
                                                        onChange={(e) => setPositionSearch(prev => ({
                                                            ...prev,
                                                            [userLevel.id]: e.target.value
                                                        }))}
                                                        className="w-full pl-9 pr-3 py-2 text-sm bg-white/60 border border-white/80 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                                                    />
                                                </div>

                                                <div className="flex gap-2 mb-3">
                                                    <button
                                                        onClick={() => handleSelectAll(userLevel.id, true)}
                                                        className="flex-1 text-xs px-2 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors font-medium"
                                                    >
                                                        Select All
                                                    </button>
                                                    <button
                                                        onClick={() => handleSelectAll(userLevel.id, false)}
                                                        className="flex-1 text-xs px-2 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors font-medium"
                                                    >
                                                        Clear
                                                    </button>
                                                </div>

                                                <div className="max-h-60 overflow-y-auto space-y-1.5">
                                                    {filteredPositionsForCard.map((position) => {
                                                        const isChecked = permissions[userLevel.id]?.[position.id] || false;
                                                        return (
                                                            <label
                                                                key={position.id}
                                                                className="flex justify-between items-center py-2.5 px-3 text-xs hover:bg-white/80 rounded-xl cursor-pointer transition-all duration-300 backdrop-blur-sm border border-transparent hover:border-white/60 hover:shadow-md group"
                                                            >
                                                                <span className="flex items-center gap-2.5 flex-1">
                                                                    {isChecked ? (
                                                                        <div className="p-1 bg-blue-100 rounded-lg">
                                                                            <Unlock size={14} className="text-blue-600" />
                                                                        </div>
                                                                    ) : (
                                                                        <div className="p-1 bg-gray-100 rounded-lg">
                                                                            <Lock size={14} className="text-gray-400" />
                                                                        </div>
                                                                    )}
                                                                    <span className={`${isChecked ? "text-gray-800 font-semibold" : "text-gray-500"} transition-colors`}>
                                                                        {position.positionName}
                                                                    </span>
                                                                </span>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isChecked}
                                                                    onChange={() => handlePermissionToggle(userLevel.id, position.id)}
                                                                    className="w-4 h-4 accent-blue-500 cursor-pointer rounded transition-transform group-hover:scale-110"
                                                                />
                                                            </label>
                                                        );
                                                    })}
                                                    {filteredPositionsForCard.length === 0 && (
                                                        <p className="text-center text-gray-500 text-xs py-4">No positions found</p>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="absolute inset-0 rounded-3xl opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none bg-gradient-to-br from-white/20 via-transparent to-transparent"></div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="bg-white/40 backdrop-blur-xl border border-white/60 rounded-2xl p-4 shadow-xl">
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-600">
                                Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                            </div>

                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => goToPage(1)}
                                    disabled={currentPage === 1}
                                    className="p-2 rounded-lg bg-white/60 hover:bg-white/80 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                >
                                    <ChevronsLeft size={18} className="text-gray-700" />
                                </button>
                                <button
                                    onClick={() => goToPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="p-2 rounded-lg bg-white/60 hover:bg-white/80 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                >
                                    <ChevronLeft size={18} className="text-gray-700" />
                                </button>

                                <div className="flex gap-1">
                                    {[...Array(totalPages)].map((_, idx) => {
                                        const pageNum = idx + 1;
                                        if (
                                            pageNum === 1 ||
                                            pageNum === totalPages ||
                                            (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                                        ) {
                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => goToPage(pageNum)}
                                                    className={`px-3 py-1.5 rounded-lg font-medium transition-all ${currentPage === pageNum
                                                        ? "bg-blue-500 text-white shadow-lg"
                                                        : "bg-white/60 hover:bg-white/80 text-gray-700"
                                                        }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        } else if (
                                            pageNum === currentPage - 2 ||
                                            pageNum === currentPage + 2
                                        ) {
                                            return (
                                                <span key={pageNum} className="px-2 py-1.5 text-gray-500">
                                                    ...
                                                </span>
                                            );
                                        }
                                        return null;
                                    })}
                                </div>

                                <button
                                    onClick={() => goToPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="p-2 rounded-lg bg-white/60 hover:bg-white/80 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                >
                                    <ChevronRight size={18} className="text-gray-700" />
                                </button>
                                <button
                                    onClick={() => goToPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                    className="p-2 rounded-lg bg-white/60 hover:bg-white/80 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                >
                                    <ChevronsRight size={18} className="text-gray-700" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}